import React, { useState, useMemo } from 'react';
import { Box, Button, Group, NumberInput, Checkbox, Card, Title, Grid, Text, Stack, SegmentedControl, ActionIcon, Tabs, Tooltip } from '@mantine/core';
import { IconTrash, IconPlus, IconList, IconEdit, IconAlertCircleFilled, IconArrowRight } from '@tabler/icons-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import Tabla from '../../../../globales/componentes/tabla/tabla';
import type { ICotizacionNBS } from '../../../../modelos/CotizadorNBS/entidades/ICotizacionNBS';
import type { IEquipoAscensor } from '../../../../modelos/CotizadorNBS/entidades/IEquipoAscensor';
import type { IOpcionesEquiposNBS } from '../../../../modelos/CotizadorNBS';
import type { IEquipoEscalera } from '../../../../modelos/CotizadorNBS/entidades/IEquipoEscalera';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';
import { useConfirmacion } from '../../../../globales/contextos/confirmacionContext';
import type { IEspecificacionesAscensor } from '../../../../modelos/CotizadorNBS/entidades/IEspecificacionesAscensor';
import { estadoInicialEspecificacionesAscensor, SECCIONES_ESPECIFICACIONES_ASCENSOR, textoCapacidadAscensor, validarAscensor } from '../../utils/especificacionesAscensor';
import { nombreCatalogo } from '../../utils/opcionesEquipos';
import { DatosAscensorVista } from './DatosAscensorVista';
import type { IEspecificacionesEscalera } from '../../../../modelos/CotizadorNBS/entidades/IEspecificacionesEscalera';
import { estadoInicialEspecificacionesEscalera, LARGO_TEXTO_ESPECIFICACION_ESCALERA, SECCIONES_ESPECIFICACIONES_ESCALERA, validarEscalera } from '../../utils/especificacionesEscalera';
import { faltantesPorPestana, type ErroresFormulario, type ValorEspecificacion } from '../../utils/camposEspecificacion';
import { CamposEspecificacionesVista } from './CamposEspecificacionesVista';
import { DatosEscaleraVista } from './DatosEscaleraVista';

/** La unidad va en el valor, no en el título de la columna: "1,6 m/s", "630 kg". */
const conUnidad = (unidad: string) => (info: { getValue: () => unknown }) => {
    const valor = info.getValue();
    if (valor === null || valor === undefined || valor === '') return '-';
    return unidad === '°' ? `${valor}°` : `${valor} ${unidad}`;
};

/** Pestañas del formulario del ascensor antes de Viáticos, en orden (el botón Siguiente avanza por ellas). */
const PESTANAS_ASCENSOR = ['generales', ...SECCIONES_ESPECIFICACIONES_ASCENSOR.map(s => s.clave)];
const PESTANAS_ESCALERA = ['generales', ...SECCIONES_ESPECIFICACIONES_ESCALERA.map(s => s.clave)];

/** Largos máximos de las especificaciones del ascensor: iguales a EspecificacionesAscensor en el back. */
const LARGO_TEXTO_ASCENSOR = 100;
const LARGO_ACABADO_ASCENSOR = 300;

/** Título de la pestaña con un ícono de alerta si quedaron campos sin diligenciar al intentar guardar. */
const TituloPestana: React.FC<{ titulo: string; faltantes: number }> = ({ titulo, faltantes }) => (
    <Group gap={6} wrap="nowrap">
        {titulo}
        {faltantes > 0 && (
            <Tooltip label={`${faltantes} ${faltantes === 1 ? 'campo pendiente' : 'campos pendientes'}`} withArrow>
                <IconAlertCircleFilled size={18} color="var(--mantine-color-red-6)" style={{ flexShrink: 0 }} />
            </Tooltip>
        )}
    </Group>
);

interface PasoEquiposVistaProps {
    cotizacion: ICotizacionNBS;
    opcionesEquipos: IOpcionesEquiposNBS | null;
    esAceptada: boolean;
    eliminarEquipo?: (id: string, tipo: 'ascensor' | 'escalera') => void;
    editarAscensor?: (equipo: IEquipoAscensor) => void;
    agregarAscensor?: (equipo: IEquipoAscensor) => Promise<void>;
    editarEscalera?: (equipo: IEquipoEscalera) => void;
    agregarEscalera?: (equipo: IEquipoEscalera) => Promise<void>;
    setEstaGuardado: React.Dispatch<React.SetStateAction<boolean>>;
    setErrorGuardado: React.Dispatch<React.SetStateAction<boolean>>;
    setEstaAutoguardando: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PasoEquiposVista: React.FC<PasoEquiposVistaProps> = ({
    cotizacion,
    opcionesEquipos,
    esAceptada,
    eliminarEquipo,
    editarAscensor,
    agregarAscensor,
    editarEscalera,
    agregarEscalera,
    setEstaGuardado,
    setErrorGuardado,
    setEstaAutoguardando
}) => {
    const { mostrarNotificacion } = useNotificaciones();
    const { confirmar } = useConfirmacion();
    const [guardandoEquipo, setGuardandoEquipo] = useState(false);

    // Actualizador de viáticos para formulario
    const actualizarViaticosAscensor = (campo: keyof IEquipoAscensor, valor: any) => {
        const nuevosDatos = { ...ascensorForm, [campo]: valor };
        setAscensorForm(nuevosDatos);
    };

    const actualizarViaticosEscalera = (campo: keyof IEquipoEscalera, valor: any) => {
        const nuevosDatos = { ...escaleraForm, [campo]: valor };
        setEscaleraForm(nuevosDatos);
    };

    // Modal state
    const [modalAbierto, setModalAbierto] = useState(false);
    const [tipoEquipo, setTipoEquipo] = useState<'ascensor' | 'escalera'>('ascensor');


    // Formularios simples de estado local
    const [ascensorForm, setAscensorForm] = useState<Partial<IEquipoAscensor>>({});
    const [escaleraForm, setEscaleraForm] = useState<Partial<IEquipoEscalera>>({});
    const [pestanaEquipo, setPestanaEquipo] = useState<string | null>('generales');
    /** Campos obligatorios sin diligenciar del formulario abierto (se marcan al intentar guardar). */
    const [erroresEquipo, setErroresEquipo] = useState<ErroresFormulario>({});
    const faltantes = tipoEquipo === 'ascensor'
        ? faltantesPorPestana(SECCIONES_ESPECIFICACIONES_ASCENSOR, erroresEquipo)
        : faltantesPorPestana(SECCIONES_ESPECIFICACIONES_ESCALERA, erroresEquipo);
    const pestanasFormulario = tipoEquipo === 'ascensor' ? PESTANAS_ASCENSOR : PESTANAS_ESCALERA;
    const siguientePestana = pestanasFormulario[pestanasFormulario.indexOf(pestanaEquipo ?? '') + 1];
    // El botón dice a qué pestaña va, para no confundirlo con el "Siguiente" del paso de la cotización.
    const tituloSiguientePestana = (tipoEquipo === 'ascensor' ? SECCIONES_ESPECIFICACIONES_ASCENSOR : SECCIONES_ESPECIFICACIONES_ESCALERA)
        .find(s => s.clave === siguientePestana)?.titulo;

    /** Abre el formulario del ascensor (vacío o con un equipo) sin errores de un intento anterior. */
    const abrirFormularioAscensor = (equipo: Partial<IEquipoAscensor>) => {
        setAscensorForm(equipo);
        setErroresEquipo({});
        setPestanaEquipo('generales');
    };

    const abrirFormularioEscalera = (equipo: Partial<IEquipoEscalera>) => {
        setEscaleraForm(equipo);
        setErroresEquipo({});
        setPestanaEquipo('generales');
    };

    // Los errores son del formulario que se intentó guardar: al cambiar de tipo de equipo se limpian.
    React.useEffect(() => setErroresEquipo({}), [tipoEquipo]);

    // Si la cotización solo tiene escaleras, mostrar directamente las escaleras.
    React.useEffect(() => {
        if (cotizacion.Ascensores.length === 0 && cotizacion.Escaleras.length > 0) setTipoEquipo('escalera');
    }, [cotizacion.Ascensores.length, cotizacion.Escaleras.length]);

    // Viáticos solo se habilita para el equipo ya guardado del tipo actual: al cambiar de tipo
    // sin haber guardado ese formulario, se vuelve a "Datos del Equipo".
    React.useEffect(() => {
        const idFormularioActual = tipoEquipo === 'ascensor' ? ascensorForm.Id : escaleraForm.Id;
        if (pestanaEquipo === 'Viaticos' && !idFormularioActual) setPestanaEquipo('generales');
    }, [tipoEquipo, ascensorForm.Id, escaleraForm.Id, pestanaEquipo]);

    // Ocultar ícono de guardado si se está editando un equipo
    React.useEffect(() => {
        if (modalAbierto) {
            setEstaGuardado(false);
            setErrorGuardado(false);
        }
    }, [ascensorForm, escaleraForm, modalAbierto]);

    const especificacionesAscensor = ascensorForm.Especificaciones ?? estadoInicialEspecificacionesAscensor;

    /** Quita el error de los campos que el usuario va diligenciando. */
    const limpiarErrores = (campos: string[]) =>
        setErroresEquipo(prev => {
            if (!campos.some(c => c in prev)) return prev;
            const siguientes = { ...prev };
            for (const c of campos) delete siguientes[c];
            return siguientes;
        });

    /** Marca los campos pendientes, abre la primera pestaña con pendientes y avisa. */
    const avisarCamposPendientes = (errores: ErroresFormulario, pestana: string) => {
        setErroresEquipo(errores);
        setPestanaEquipo(pestana);
        mostrarNotificacion(EnumTipoNotificacion.Warning, "Campos requeridos (*)", "Revisa los campos marcados en rojo antes de guardar.");
    };

    const cambiarDatosAscensor = (cambios: Partial<IEquipoAscensor>) => {
        setAscensorForm(prev => ({ ...prev, ...cambios }));
        limpiarErrores(Object.keys(cambios));
    };

    const cambiarEspecificacionAscensor = (campo: keyof IEspecificacionesAscensor, valor: ValorEspecificacion) => {
        setAscensorForm(prev => ({
            ...prev,
            Especificaciones: { ...(prev.Especificaciones ?? estadoInicialEspecificacionesAscensor), [campo]: valor }
        }));
        limpiarErrores([campo]);
    };

    const especificacionesEscalera = escaleraForm.Especificaciones ?? estadoInicialEspecificacionesEscalera;
    const cambiarDatosEscalera = (cambios: Partial<IEquipoEscalera>) => {
        setEscaleraForm(prev => ({ ...prev, ...cambios }));
        limpiarErrores(Object.keys(cambios));
    };
    const cambiarEspecificacionEscalera = (campo: keyof IEspecificacionesEscalera, valor: ValorEspecificacion) => {
        setEscaleraForm(prev => ({
            ...prev,
            Especificaciones: { ...(prev.Especificaciones ?? estadoInicialEspecificacionesEscalera), [campo]: valor }
        }));
        limpiarErrores([campo]);
    };

    /** Cancelar, Siguiente (si hay otra pestaña antes de Viáticos) y Guardar: el equipo se guarda completo. */
    const botonesFormulario = () => (
        <Group justify="flex-end" gap="xs" mt="md">
            <Button variant="default" size="xs" radius="xl" onClick={() => setModalAbierto(false)} disabled={guardandoEquipo}>Cancelar</Button>
            {siguientePestana && (
                <Button variant="light" color="primary" size="xs" radius="xl" rightSection={<IconArrowRight size={14} />}
                    onClick={() => setPestanaEquipo(siguientePestana)} disabled={guardandoEquipo}>Ir a {tituloSiguientePestana}</Button>
            )}
            <Button color="primary" size="xs" radius="xl" onClick={handleGuardarEquipo} loading={guardandoEquipo}>Guardar</Button>
        </Group>
    );

    const handleGuardarEquipo = async () => {
        if (tipoEquipo === 'ascensor') {
            const { errores, primeraPestanaPendiente } = validarAscensor(ascensorForm);
            if (primeraPestanaPendiente) {
                avisarCamposPendientes(errores, primeraPestanaPendiente);
                return;
            }
            setGuardandoEquipo(true);
            setEstaAutoguardando(true);
            setEstaGuardado(false);
            setErrorGuardado(false);
            const equipoData = {
                Id: ascensorForm.Id || undefined,
                Nombre: ascensorForm.Nombre,
                Tipo: ascensorForm.Tipo,
                Uso: ascensorForm.Uso,
                Producto: ascensorForm.Producto,
                Capacidad: ascensorForm.Capacidad,
                Pasajeros: ascensorForm.Pasajeros,
                VelocidadIngreso: ascensorForm.VelocidadIngreso,
                OperadorPuerta: ascensorForm.OperadorPuerta,
                Paradas: ascensorForm.Paradas,
                Maquina: ascensorForm.Maquina,
                PotenciaMotor: ascensorForm.PotenciaMotor,
                Especificaciones: especificacionesAscensor,
                Cantidad: ascensorForm.Cantidad,
                CostoFCA: ascensorForm.CostoFCA,
                OtrasCiudades: ascensorForm.OtrasCiudades
            } as IEquipoAscensor;

            try {
                if (ascensorForm.Id && editarAscensor) {
                    await editarAscensor(equipoData);
                    setEstaGuardado(true);
                } else if (agregarAscensor) {
                    const result: any = await agregarAscensor(equipoData);
                    const responseData = result?.data ?? result?.Data ?? result;
                    const notifType = result?.NotificationType ?? result?.notificationType;
                    const eqId = responseData?.equipoId ?? responseData?.EquipoId;

                    if (result !== undefined && result !== null && notifType === 1 && responseData && eqId) {
                        setAscensorForm(prev => ({ 
                            ...prev, 
                            Id: eqId, 
                            DiasViaticos: responseData.diasInstalacion || responseData.DiasInstalacion, 
                            ValorViaticos: responseData.viaticos || responseData.Viaticos, 
                            ValorDesplazamientos: responseData.desplazamientos || responseData.Desplazamientos,
                            ValorVuelos: responseData.vuelos ?? responseData.Vuelos
                        }));
                        setPestanaEquipo('Viaticos');
                    }
                    setEstaGuardado(true);
                }
            } catch (e) {
                // El CV ya mostró la notificación de error
                setErrorGuardado(true);
            } finally {
                setGuardandoEquipo(false);
                setEstaAutoguardando(false);
            }
        } else if (tipoEquipo === 'escalera') {
            const { errores, primeraPestanaPendiente } = validarEscalera(escaleraForm);
            if (primeraPestanaPendiente) {
                avisarCamposPendientes(errores, primeraPestanaPendiente);
                return;
            }
            setGuardandoEquipo(true);
            setEstaAutoguardando(true);
            setEstaGuardado(false);
            setErrorGuardado(false);
            const equipoData = {
                Id: escaleraForm.Id || undefined,
                Nombre: escaleraForm.Nombre,
                Tipo: escaleraForm.Tipo,
                Producto: escaleraForm.Producto,
                Contenedores: escaleraForm.Contenedores,
                VelocidadIngreso: escaleraForm.VelocidadIngreso,
                Altura: escaleraForm.Altura,
                AnchoPasos: escaleraForm.AnchoPasos,
                Inclinacion: escaleraForm.Inclinacion,
                PotenciaMotor: escaleraForm.PotenciaMotor,
                Especificaciones: especificacionesEscalera,
                Cantidad: escaleraForm.Cantidad,
                CostoFCA: escaleraForm.CostoFCA,
                OtrasCiudades: escaleraForm.OtrasCiudades
            } as IEquipoEscalera;

            try {
                if (escaleraForm.Id && editarEscalera) {
                    await editarEscalera(equipoData);
                    setEstaGuardado(true);
                } else if (agregarEscalera) {
                    const result: any = await agregarEscalera(equipoData);
                    const responseData = result?.data ?? result?.Data ?? result;
                    const notifType = result?.NotificationType ?? result?.notificationType;
                    const eqId = responseData?.equipoId ?? responseData?.EquipoId;

                    if (result !== undefined && result !== null && notifType === 1 && responseData && eqId) {
                        setEscaleraForm(prev => ({ 
                            ...prev, 
                            Id: eqId, 
                            DiasViaticos: responseData.diasInstalacion || responseData.DiasInstalacion, 
                            ValorViaticos: responseData.viaticos || responseData.Viaticos, 
                            ValorDesplazamientos: responseData.desplazamientos || responseData.Desplazamientos,
                            ValorVuelos: responseData.vuelos ?? responseData.Vuelos
                        }));
                        setPestanaEquipo('Viaticos');
                    }
                    setEstaGuardado(true);
                }
            } catch (e) {
                // El CV ya mostró la notificación de error
                setErrorGuardado(true);
            } finally {
                setGuardandoEquipo(false);
                setEstaAutoguardando(false);
            }
        }
    };

    const columnasAscensoresData = useMemo<ColumnDef<IEquipoAscensor>[]>(() => [
        {
            Id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => (
                <Group gap={4} justify="center">
                    <Tooltip label="Editar" style={{ fontSize: '10px', padding: '3px 5px' }} withArrow>
                        <ActionIcon
                            color='var(--mantine-color-primary-0)'
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            disabled={esAceptada}
                            onClick={() => {
                                setTipoEquipo('ascensor');
                                abrirFormularioAscensor(row.original);
                                setModalAbierto(true);
                            }}
                        >
                            <IconEdit size={18} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip style={{ fontSize: '10px', padding: '3px 5px' }} label="Eliminar" withArrow>
                        <ActionIcon
                            color="var(--mantine-color-primary-0)"
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            disabled={esAceptada}
                            onClick={async () => {
                                const confirmado = await confirmar({
                                    titulo: 'Eliminar Equipo',
                                    mensaje: '¿Estás seguro de eliminar este equipo?',
                                    tipo: EnumTipoNotificacion.Warning,
                                    textoAceptar: 'Eliminar',
                                    textoCancelar: 'Cancelar',
                                    opcionCancelar: true
                                });
                                if (confirmado) {
                                    if (eliminarEquipo) eliminarEquipo(row.original.Id, 'ascensor');
                                }
                            }}
                        >
                            <IconTrash size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            )
        },
        { accessorKey: 'Nombre', header: 'Nombre' },
        { accessorKey: 'Tipo', header: 'Tipo' },
        { accessorKey: 'Uso', header: 'Uso', cell: (info) => info.getValue() || '-' },
        { id: 'modelo', header: 'Modelo', accessorFn: (a) => nombreCatalogo(opcionesEquipos?.ascensores?.productos, a.Producto, a.ProductoNombre) },
        { id: 'capacidad', header: 'Capacidad', accessorFn: (a) => textoCapacidadAscensor(a.Capacidad, a.Pasajeros) },
        { accessorKey: 'VelocidadIngreso', header: 'Velocidad', cell: conUnidad('m/s') },
        { id: 'operadorPuerta', header: 'Op. Puerta', accessorFn: (a) => nombreCatalogo(opcionesEquipos?.ascensores?.operadoresPuerta, a.OperadorPuerta, a.OperadorPuertaNombre) },
        { accessorKey: 'Paradas', header: 'Paradas' },
        { id: 'maquina', header: 'Máquina', accessorFn: (a) => nombreCatalogo(opcionesEquipos?.ascensores?.maquinas, a.Maquina, a.MaquinaNombre) },
        { accessorKey: 'PotenciaMotor', header: 'Potencia Motor', cell: conUnidad('kW') },
        { accessorKey: 'Cantidad', header: 'Cantidad' },
        { accessorKey: 'CostoFCA', header: 'Costo', cell: (info) => `$ ${(Number(info.getValue()) || 0).toLocaleString('es-CO')} USD` }
    ], [eliminarEquipo, opcionesEquipos]);

    const tablaAscensoresData = useReactTable({
        data: cotizacion.Ascensores,
        columns: columnasAscensoresData,
        getCoreRowModel: getCoreRowModel()
    });

    const columnasEscalerasData = useMemo<ColumnDef<IEquipoEscalera>[]>(() => [
        {
            Id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => (
                <Group gap={4} justify="center">
                    <Tooltip label="Editar" style={{ fontSize: '10px', padding: '3px 5px' }} withArrow>
                        <ActionIcon
                            color='var(--mantine-color-primary-0)'
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            disabled={esAceptada}
                            onClick={() => {
                                setTipoEquipo('escalera');
                                abrirFormularioEscalera(row.original);
                                setModalAbierto(true);
                            }}
                        >
                            <IconEdit size={18} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip style={{ fontSize: '10px', padding: '3px 5px' }} label="Eliminar" withArrow>
                        <ActionIcon
                            color="var(--mantine-color-primary-0)"
                            variant="transparent"
                            radius="sm"
                            size="sm"
                            disabled={esAceptada}
                            onClick={async () => {
                                const confirmado = await confirmar({
                                    titulo: 'Eliminar Equipo',
                                    mensaje: '¿Estás seguro de eliminar este equipo?',
                                    tipo: EnumTipoNotificacion.Warning,
                                    textoAceptar: 'Eliminar',
                                    textoCancelar: 'Cancelar',
                                    opcionCancelar: true
                                });
                                if (confirmado) {
                                    if (eliminarEquipo) eliminarEquipo(row.original.Id, 'escalera');
                                }
                            }}
                        >
                            <IconTrash size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            )
        },
        { accessorKey: 'Nombre', header: 'Nombre' },
        { accessorKey: 'Tipo', header: 'Tipo' },
        { id: 'modelo', header: 'Modelo', accessorFn: (e) => nombreCatalogo(opcionesEquipos?.escaleras?.productos, e.Producto, e.ProductoNombre) },
        { accessorKey: 'Contenedores', header: 'Contenedores' },
        { accessorKey: 'VelocidadIngreso', header: 'Velocidad', cell: conUnidad('m/s') },
        { accessorKey: 'Altura', header: 'Altura', cell: conUnidad('mm') },
        { accessorKey: 'AnchoPasos', header: 'Ancho de pasos', cell: conUnidad('mm') },
        { accessorKey: 'Inclinacion', header: 'Inclinación', cell: conUnidad('°') },
        { accessorKey: 'PotenciaMotor', header: 'Potencia Motor', cell: conUnidad('kW') },
        { accessorKey: 'Cantidad', header: 'Cantidad' },
        { accessorKey: 'CostoFCA', header: 'Costo', cell: (info) => `$ ${(Number(info.getValue()) || 0).toLocaleString('es-CO')} USD` }
    ], [eliminarEquipo, opcionesEquipos]);

    const tablaEscalerasData = useReactTable({
        data: cotizacion.Escaleras,
        columns: columnasEscalerasData,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <Stack gap="xl">
                            <Stack>
                                {!modalAbierto ? (
                                    <Tabs defaultValue="equipos">
                                        <Tabs.List mb="md">
                                            <Tabs.Tab value="equipos">Equipos Agregados</Tabs.Tab>

                                        </Tabs.List>

                                        <Tabs.Panel value="equipos">
                                            <Box mt="md" mb="md">
                                                <Group justify="space-between" mb="md">
                                                    <SegmentedControl
                                                        value={tipoEquipo}
                                                        onChange={(val) => setTipoEquipo(val as 'ascensor' | 'escalera')}
                                                        data={[
                                                            { label: 'Ascensores', value: 'ascensor' },
                                                            { label: 'Escaleras', value: 'escalera' }
                                                        ]}
                                                    />
                                                    <Button size="xs" radius="xl" color="primary" leftSection={<IconPlus size={16} />} disabled={esAceptada} onClick={() => {
                                                        abrirFormularioAscensor({});
                                                        abrirFormularioEscalera({});
                                                        setEstaGuardado(false);
                                                        setModalAbierto(true);
                                                    }}>
                                                        Agregar Equipo
                                                    </Button>
                                                </Group>

                                                {tipoEquipo === 'ascensor' ? (
                                                    <div style={{ overflowX: 'auto', maxHeight: '300px' }}>
                                                        <Tabla tabla={tablaAscensoresData} />
                                                    </div>
                                                ) : (
                                                    <div style={{ overflowX: 'auto', maxHeight: '300px' }}>
                                                        <Tabla tabla={tablaEscalerasData} />
                                                    </div>
                                                )}
                                            </Box>
                                        </Tabs.Panel>


                                    </Tabs>
                                ) : (
                                    <Grid mt="md">
                                        <Grid.Col span={12}>
                                            <Card withBorder radius="md">
                                                <Group justify="space-between" align="center" mb="md">
                                                    <Group>
                                                        <Title order={6}>Agregar Equipo</Title>
                                                        <SegmentedControl
                                                            size="xs"
                                                            value={tipoEquipo}
                                                            onChange={(val) => setTipoEquipo(val as 'ascensor' | 'escalera')}
                                                            data={[
                                                                { label: 'Ascensor', value: 'ascensor' },
                                                                { label: 'Escalera', value: 'escalera' }
                                                            ]}
                                                            disabled={guardandoEquipo}
                                                        />
                                                    </Group>
                                                    <Button size="xs" radius="xl" color="primary" variant="light" leftSection={<IconList size={16} />} onClick={() => setModalAbierto(false)} disabled={guardandoEquipo}>Ver Equipos</Button>
                                                </Group>
                                                {tipoEquipo === 'ascensor' && (
                                                    <Tabs value={pestanaEquipo} onChange={setPestanaEquipo}>
                                                        <Tabs.List mb="md">
                                                            <Tabs.Tab value="generales"><TituloPestana titulo="Datos del Equipo" faltantes={faltantes.generales} /></Tabs.Tab>
                                                            {SECCIONES_ESPECIFICACIONES_ASCENSOR.map(seccion => (
                                                                <Tabs.Tab key={seccion.clave} value={seccion.clave}><TituloPestana titulo={seccion.titulo} faltantes={faltantes[seccion.clave]} /></Tabs.Tab>
                                                            ))}
                                                            <Tabs.Tab value="Viaticos" disabled={!ascensorForm.Id}>Viáticos</Tabs.Tab>
                                                        </Tabs.List>

                                                        <Tabs.Panel value="generales">
                                                            <DatosAscensorVista
                                                                form={ascensorForm}
                                                                alCambiar={cambiarDatosAscensor}
                                                                opciones={opcionesEquipos}
                                                                errores={erroresEquipo}
                                                                deshabilitado={guardandoEquipo} />
                                                        </Tabs.Panel>

                                                        {SECCIONES_ESPECIFICACIONES_ASCENSOR.map(seccion => (
                                                            <Tabs.Panel key={seccion.clave} value={seccion.clave}>
                                                                <CamposEspecificacionesVista
                                                                    seccion={seccion}
                                                                    valores={especificacionesAscensor}
                                                                    alCambiar={cambiarEspecificacionAscensor}
                                                                    errores={erroresEquipo}
                                                                    obligatorios
                                                                    largoTexto={LARGO_TEXTO_ASCENSOR}
                                                                    largoAcabado={LARGO_ACABADO_ASCENSOR}
                                                                    deshabilitado={guardandoEquipo}
                                                                    opciones={{ maniobras: opcionesEquipos?.ascensores?.maniobras, aperturas: opcionesEquipos?.ascensores?.aperturas }} />
                                                            </Tabs.Panel>
                                                        ))}

                                                        {/* Un solo guardado para todas las pestañas: el equipo se guarda completo. */}
                                                        {pestanaEquipo !== 'Viaticos' && botonesFormulario()}

                                                        <Tabs.Panel value="Viaticos">
                                                            <Grid>
                                                                <Grid.Col span={12}>
                                                                    <Checkbox 
                                                                        label="Fuera de Ciudad base (Otras ciudades)" 
                                                                        checked={ascensorForm.OtrasCiudades || false}
                                                                        onChange={async (e) => {
                                                                            const newVal = e.currentTarget.checked;
                                                                            actualizarViaticosAscensor('OtrasCiudades', newVal);
                                                                            if (ascensorForm.Id && editarAscensor) {
                                                                                const updated: any = await editarAscensor({ ...ascensorForm, OtrasCiudades: newVal } as IEquipoAscensor);
                                                                                const updatedData = updated?.data ?? updated?.Data ?? updated;
                                                                                if (updatedData) {
                                                                                    setAscensorForm(prev => ({ 
                                                                                        ...prev, 
                                                                                        DiasViaticos: updatedData.diasInstalacion || updatedData.DiasInstalacion, 
                                                                                        ValorViaticos: updatedData.viaticos || updatedData.Viaticos, 
                                                                                        ValorDesplazamientos: updatedData.desplazamientos || updatedData.Desplazamientos,
                            ValorVuelos: updatedData.vuelos ?? updatedData.Vuelos 
                                                                                    }));
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                </Grid.Col>
                                                                <Grid.Col span={12}>
                                                                    {ascensorForm.OtrasCiudades && (
                                                                        <Grid mt="sm">
                                                                            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
                                                                                <NumberInput size="xs" label="Días Instalación" value={ascensorForm.DiasViaticos || 0} readOnly disabled hideControls />
                                                                            </Grid.Col>
                                                                            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
                                                                                <NumberInput size="xs" label="Viáticos" value={ascensorForm.ValorViaticos || 0} thousandSeparator leftSection={<Text size="xs" c="dimmed">$</Text>} rightSection={<Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>{cotizacion.MonedaLocal ?? 'COP'}</Text>} rightSectionWidth={40} readOnly disabled hideControls />
                                                                            </Grid.Col>
                                                                            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
                                                                                <NumberInput size="xs" label="Desplazamientos" value={ascensorForm.ValorDesplazamientos || 0} thousandSeparator leftSection={<Text size="xs" c="dimmed">$</Text>} rightSection={<Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>{cotizacion.MonedaLocal ?? 'COP'}</Text>} rightSectionWidth={40} readOnly disabled hideControls />
                                                                            </Grid.Col>
                                                                        </Grid>
                                                                    )}
                                                                </Grid.Col>
                                                                <Grid.Col span={12} mt="md">
                                                                    <Text size="sm" c="dimmed">Estos valores son informativos y se calculan automáticamente.</Text>
                                                                    <Group justify="flex-end" mt="xl">
                                                                        <Button variant="outline" onClick={() => { setModalAbierto(false); setPestanaEquipo('generales'); setAscensorForm({}); setEscaleraForm({}); setEstaGuardado(false); }}>
                                                                            Finalizar y Volver a la Lista
                                                                        </Button>
                                                                    </Group>
                                                                </Grid.Col>
                                                            </Grid>
                                                        </Tabs.Panel>
                                                    </Tabs>
                                                )}

                                                {tipoEquipo === 'escalera' && (
                                                    <Tabs value={pestanaEquipo} onChange={setPestanaEquipo}>
                                                        <Tabs.List mb="md">
                                                            <Tabs.Tab value="generales"><TituloPestana titulo="Datos del Equipo" faltantes={faltantes.generales} /></Tabs.Tab>
                                                            {SECCIONES_ESPECIFICACIONES_ESCALERA.map(seccion => (
                                                                <Tabs.Tab key={seccion.clave} value={seccion.clave}><TituloPestana titulo={seccion.titulo} faltantes={faltantes[seccion.clave]} /></Tabs.Tab>
                                                            ))}
                                                            <Tabs.Tab value="Viaticos" disabled={!escaleraForm.Id}>Viáticos</Tabs.Tab>
                                                        </Tabs.List>

                                                        <Tabs.Panel value="generales">
                                                            <DatosEscaleraVista
                                                                form={escaleraForm}
                                                                alCambiar={cambiarDatosEscalera}
                                                                opciones={opcionesEquipos}
                                                                errores={erroresEquipo}
                                                                deshabilitado={guardandoEquipo} />
                                                        </Tabs.Panel>

                                                        {/* Por ahora todas obligatorias, como en ascensores (pendiente confirmar con el cliente). */}
                                                        {SECCIONES_ESPECIFICACIONES_ESCALERA.map(seccion => (
                                                            <Tabs.Panel key={seccion.clave} value={seccion.clave}>
                                                                <CamposEspecificacionesVista
                                                                    seccion={seccion}
                                                                    valores={especificacionesEscalera}
                                                                    alCambiar={cambiarEspecificacionEscalera}
                                                                    errores={erroresEquipo}
                                                                    obligatorios
                                                                    largoTexto={LARGO_TEXTO_ESPECIFICACION_ESCALERA}
                                                                    deshabilitado={guardandoEquipo} />
                                                            </Tabs.Panel>
                                                        ))}

                                                        {pestanaEquipo !== 'Viaticos' && botonesFormulario()}

                                                        <Tabs.Panel value="Viaticos">
                                                            <Grid>
                                                                <Grid.Col span={12}>
                                                                    <Checkbox 
                                                                        label="Fuera de Ciudad base (Otras ciudades)" 
                                                                        checked={escaleraForm.OtrasCiudades || false}
                                                                        onChange={async (e) => {
                                                                            const newVal = e.currentTarget.checked;
                                                                            actualizarViaticosEscalera('OtrasCiudades', newVal);
                                                                            if (escaleraForm.Id && editarEscalera) {
                                                                                const updated: any = await editarEscalera({ ...escaleraForm, OtrasCiudades: newVal } as IEquipoEscalera);
                                                                                const updatedData = updated?.data ?? updated?.Data ?? updated;
                                                                                if (updatedData) {
                                                                                    setEscaleraForm(prev => ({ 
                                                                                        ...prev, 
                                                                                        DiasViaticos: updatedData.diasInstalacion || updatedData.DiasInstalacion, 
                                                                                        ValorViaticos: updatedData.viaticos || updatedData.Viaticos, 
                                                                                        ValorDesplazamientos: updatedData.desplazamientos || updatedData.Desplazamientos,
                            ValorVuelos: updatedData.vuelos ?? updatedData.Vuelos 
                                                                                    }));
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                </Grid.Col>
                                                                <Grid.Col span={12}>
                                                                    {escaleraForm.OtrasCiudades && (
                                                                        <Grid mt="sm">
                                                                            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
                                                                                <NumberInput size="xs" label="Días Instalación" value={escaleraForm.DiasViaticos || 0} readOnly disabled hideControls />
                                                                            </Grid.Col>
                                                                            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
                                                                                <NumberInput size="xs" label="Viáticos" value={escaleraForm.ValorViaticos || 0} thousandSeparator leftSection={<Text size="xs" c="dimmed">$</Text>} rightSection={<Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>{cotizacion.MonedaLocal ?? 'COP'}</Text>} rightSectionWidth={40} readOnly disabled hideControls />
                                                                            </Grid.Col>
                                                                            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
                                                                                <NumberInput size="xs" label="Desplazamientos" value={escaleraForm.ValorDesplazamientos || 0} thousandSeparator leftSection={<Text size="xs" c="dimmed">$</Text>} rightSection={<Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>{cotizacion.MonedaLocal ?? 'COP'}</Text>} rightSectionWidth={40} readOnly disabled hideControls />
                                                                            </Grid.Col>
                                                                        </Grid>
                                                                    )}
                                                                </Grid.Col>
                                                                <Grid.Col span={12} mt="md">
                                                                    <Text size="sm" c="dimmed">Estos valores son informativos y se calculan automáticamente.</Text>
                                                                    <Group justify="flex-end" mt="xl">
                                                                        <Button variant="outline" onClick={() => { setModalAbierto(false); setPestanaEquipo('generales'); setAscensorForm({}); setEscaleraForm({}); setEstaGuardado(false); }}>
                                                                            Finalizar y Volver a la Lista
                                                                        </Button>
                                                                    </Group>
                                                                </Grid.Col>
                                                            </Grid>
                                                        </Tabs.Panel>
                                                    </Tabs>
                                                )}
                                            </Card>
                                        </Grid.Col>

                                    </Grid>
                                )}
                            </Stack>

        </Stack>
    );
};
